import { useState } from 'react';
import clsx from 'clsx';
import type { AIFlag } from '@/types/db';
import {
  BlockIcon,
  InfoIcon,
  WarnIcon,
} from '@/components/icons/flag-icons';

const STYLES: Record<AIFlag['severity'], string> = {
  info: 'bg-blue-50 text-flag-info border-flag-info/20',
  warn: 'bg-amber-50 text-flag-warn border-flag-warn/30',
  block: 'bg-red-50 text-flag-block border-flag-block/40',
};

const ICONS = {
  info: InfoIcon,
  warn: WarnIcon,
  block: BlockIcon,
} as const;

const ICON_COLORS: Record<AIFlag['severity'], string> = {
  info: 'text-flag-info',
  warn: 'text-flag-warn',
  block: 'text-flag-block',
};

const SEVERITY_SHORT: Record<AIFlag['severity'], string> = {
  block: 'Critical',
  warn: 'Check this',
  info: 'Note',
};

export function FlagChip({ flag, onResolve }: { flag: AIFlag; onResolve?: () => void }) {
  const Icon = ICONS[flag.severity];
  if (onResolve) {
    return (
      <div className="inline-flex items-center">
        <span
          className={clsx(
            'inline-flex items-center gap-1 rounded-l-full border-y border-l px-2 py-0.5 text-xs font-medium',
            STYLES[flag.severity],
          )}
        >
          <Icon className="h-3.5 w-3.5 shrink-0" />
          {flag.message}
        </span>
        <button
          type="button"
          onClick={onResolve}
          aria-label="Resolve flag"
          className={clsx(
            'min-h-tap min-w-[36px] rounded-r-full border-y border-r px-2 inline-flex items-center justify-center hover:bg-black/10 active:bg-black/20',
            STYLES[flag.severity],
          )}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-3 w-3"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>
      </div>
    );
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
        STYLES[flag.severity],
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      {flag.message}
    </span>
  );
}

export function FlagList({ flags, onResolve }: { flags: AIFlag[]; onResolve?: (code: string) => void }) {
  if (flags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {flags.map((flag, i) => (
        <FlagChip
          key={`${flag.code}-${i}`}
          flag={flag}
          onResolve={onResolve ? () => onResolve(flag.code) : undefined}
        />
      ))}
    </div>
  );
}

export function ResolvedFlagsGroup({ flags }: { flags: AIFlag[] }) {
  const [open, setOpen] = useState(false);
  if (flags.length === 0) return null;
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="min-h-[44px] min-w-[44px] flex items-center gap-1.5 text-xs text-ink-subtle hover:text-ink-muted"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={clsx('h-3 w-3 transition-transform duration-150', open && 'rotate-180')}
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
        {flags.length === 1 ? '1 resolved flag' : `${flags.length} resolved flags`}
      </button>
      {open && (
        <div className="mt-1.5 flex flex-wrap gap-1.5 opacity-70">
          {flags.map((f, i) => (
            <FlagChip key={`${f.code}-${i}`} flag={f} />
          ))}
        </div>
      )}
    </div>
  );
}

export function FlagInlineChip({ flag }: { flag: AIFlag }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = ICONS[flag.severity];
  return (
    <div className="inline-flex flex-col gap-0.5">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className={clsx(
          'inline-flex items-center gap-1 self-start rounded-full border px-2.5 py-1 text-xs font-semibold',
          STYLES[flag.severity],
        )}
      >
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {SEVERITY_SHORT[flag.severity]}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={clsx(
            'h-3 w-3 transition-transform duration-150',
            expanded && 'rotate-180',
          )}
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {expanded && (
        <p className={clsx('text-xs px-1 leading-relaxed', ICON_COLORS[flag.severity])}>
          {flag.message}
        </p>
      )}
    </div>
  );
}

export function FieldFlags({ flags }: { flags: AIFlag[] }) {
  if (flags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1.5">
      {flags.map((flag, i) => (
        <FlagInlineChip key={`${flag.code}-${i}`} flag={flag} />
      ))}
    </div>
  );
}

export function FieldFlagsGroup({
  flags,
  resolvedCodes = new Set(),
  onResolve,
}: {
  flags: AIFlag[];
  resolvedCodes?: Set<string>;
  onResolve?: (code: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  if (flags.length === 0) return null;

  const allResolved = flags.every((f) => resolvedCodes.has(f.code));
  const severity = flags.some((f) => f.severity === 'block')
    ? 'block'
    : flags.some((f) => f.severity === 'warn')
      ? 'warn'
      : 'info';
  const Icon = ICONS[severity];

  const chipLabel = allResolved
    ? '✓ Checked'
    : severity === 'block'
      ? 'Critical'
      : flags.length === 1
        ? '1 to check'
        : `${flags.length} need checking`;

  const chipStyle = allResolved
    ? 'bg-green-50 text-green-700 border-green-200'
    : STYLES[severity];

  return (
    <div className="flex flex-col gap-1 mt-1.5">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className={clsx(
          'inline-flex items-center gap-1.5 self-start rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
          chipStyle,
        )}
      >
        {allResolved ? (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-3 w-3 shrink-0"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        )}
        {chipLabel}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={clsx(
            'h-3 w-3 transition-transform duration-150',
            expanded && 'rotate-180',
          )}
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {expanded && (
        <div className="rounded-lg border border-border bg-canvas p-3 mt-0.5">
          <ul className="flex flex-col gap-2">
            {flags.map((flag, i) => {
              const isResolved = resolvedCodes.has(flag.code);
              return (
                <li
                  key={`${flag.code}-${i}`}
                  className={clsx(
                    'flex items-start gap-2 text-xs leading-relaxed',
                    isResolved && 'opacity-60',
                  )}
                >
                  <span
                    className={clsx(
                      'h-1.5 w-1.5 rounded-full mt-1.5 shrink-0',
                      isResolved ? 'bg-green-500' : ICON_COLORS[flag.severity],
                    )}
                    aria-hidden="true"
                  />
                  <span
                    className={clsx(
                      'flex-1',
                      isResolved && 'line-through text-ink-muted',
                    )}
                  >
                    {flag.message}
                  </span>
                  {onResolve && !isResolved && (
                    <button
                      type="button"
                      onClick={() => onResolve(flag.code)}
                      aria-label={`Mark as checked: ${flag.message}`}
                      className="min-h-[44px] min-w-[44px] rounded-full flex items-center justify-center hover:bg-black/10 active:bg-black/15 shrink-0 -mr-2"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-3.5 w-3.5 text-ink-muted"
                        aria-hidden="true"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
